import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  DestructiveConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  useStateWithSetAction,
  VirtualizedDataTable,
  VirtualizedDataTableProps,
} from "@tellescope/ui";
import { SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useEnduserTable } from "../../hooks/useEnduserTable";
import { ContactForm } from "../../components/ContactForm";
import { useTranslation } from "react-i18next";
import { Navigate, Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Table, Trash2 } from "lucide-react";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { useAppPermissionQuery } from "../../permissions";
import { useForm } from "@tellescope/forms";
import { DEFAULT_CONTACTS_VIEW, useContactsDefaultViewShallowStore, useSidebarShallowStore } from "../../store";
import { ContactsTableConfigDialog } from "../../components/contacts/ContactsTableConfigDialog";
import { ConfigureTableViewButton } from "../../components/tables/ConfigureTableViewButton";
import { buildContactTimelinePath } from "./build-contact-timeline-path";

export const ContactsPage = ({ maxTabCount = 3 }: { maxTabCount?: number } = {}) => {
  const { t } = useTranslation("contacts");
  const navigate = useNavigate();
  const params = useParams();
  const viewId = params.viewId;
  const { lastSelectedViewId, setLastSelectedViewId } = useContactsDefaultViewShallowStore(
    "lastSelectedViewId",
    "setLastSelectedViewId"
  );

  // useEffect is justified here because this store is a localStorage store that we want to sync to
  useEffect(() => {
    if (viewId && viewId !== lastSelectedViewId) {
      setLastSelectedViewId(viewId);
    }
  }, [viewId, lastSelectedViewId, setLastSelectedViewId]);

  const setViewId = useCallback(
    (idOrUpdater: SetStateAction<string>) => {
      const newId = typeof idOrUpdater === "function" ? idOrUpdater(viewId ?? "all") : idOrUpdater;
      navigate(`/contacts/views/${newId}`);
    },
    [navigate, viewId]
  );

  const { openSidebar } = useSidebarShallowStore("openSidebar");
  const contactsTable = useEnduserTable({
    viewId,
    setViewId,
    onSelectEnduser: (enduserId) => openSidebar("ContactDetails", { enduserId }),
    onOpenTimeline: (enduserId) => {
      openSidebar("ContactDetails", { enduserId });
      navigate(buildContactTimelinePath(enduserId));
    },
  });

  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [isAddViewDialogOpen, setIsAddViewDialogOpen] = useStateWithSetAction(false, (isOpen) => {
    if (!isOpen) {
      addViewForm.resetForm();
      setIsSaveAsView(false);
    }
  });
  const [isSaveAsView, setIsSaveAsView] = useState(false);
  const [viewConfigDialogIsOpen, setViewConfigDialogIsOpen] = useState(false);
  const [isOtherViewsDialogOpen, setIsOtherViewsDialogOpen] = useState(false);
  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);

  const addViewForm = useForm({
    fields: {
      title: {
        type: "text",
        required: true,
        initialValue: "",
      },
    },
    validateOn: "blur",
    onSubmit: (values) => {
      if (!values.title) return;

      const saveAsFilter = isSaveAsView ? contactsTable.selectedView.filter : undefined;

      contactsTable.createTableViewMutation.mutateAsync(
        {
          page: `${contactsTable.TELLESCOPE_2_TABLE_VIEW_PREFIX}Endusers`,
          title: values.title,
          columns: isSaveAsView ? contactsTable.selectedView.columns : DEFAULT_CONTACTS_VIEW.columns,
          ...(saveAsFilter !== undefined ? { filter: saveAsFilter } : undefined),
        },
        {
          onSuccess: (res) => {
            if (isSaveAsView) {
              contactsTable.resetDefaultView();
            }
            navigate(`/contacts/views/${res.id}`);
          },
        }
      );
      setIsAddViewDialogOpen(false);
    },
  });

  const {
    delete: { access: canDelete },
  } = useAppPermissionQuery({
    model: "table_views",
    actions: ["delete", "create"],
  });

  const {
    create: { access: canCreate },
  } = useAppPermissionQuery({
    model: "endusers",
    actions: ["create"],
  });

  const { viewsToDisplay, rolledUpViews } = contactsTable.views.reduce<{
    viewsToDisplay: TellescopeModelFromName<"table_views">[];
    rolledUpViews: TellescopeModelFromName<"table_views">[];
  }>(
    (acc, curr) => {
      if (curr.id === viewId) {
        if (acc.viewsToDisplay.length === maxTabCount - 1) {
          const viewToMove = acc.viewsToDisplay.pop();
          if (viewToMove) acc.rolledUpViews.push(viewToMove);
          acc.viewsToDisplay.push(curr);
          return acc;
        }

        acc.viewsToDisplay.push(curr);
        return acc;
      }

      if (acc.viewsToDisplay.length < maxTabCount - 1) {
        acc.viewsToDisplay.push(curr);
      } else {
        acc.rolledUpViews.push(curr);
      }

      return acc;
    },
    { viewsToDisplay: [], rolledUpViews: [] }
  );

  const activeFiltersCount = contactsTable.activeFiltersCount;

  const actionButtons = useMemo<VirtualizedDataTableProps<TellescopeModelFromName<"endusers">>["actionButtons"]>(() => {
    const result: NonNullable<VirtualizedDataTableProps<TellescopeModelFromName<"endusers">>["actionButtons"]> =
      contactsTable.viewId === "all" || !canDelete
        ? []
        : [
            {
              icon: Trash2,
              label: "",
              variant: "destructive",
              hoverLabel: t("Delete View"),
              onClick: () => setDeleteDialogIsOpen(true),
            },
          ];

    const hasSorting =
      (!activeFiltersCount && contactsTable.isDefaultViewModified) ||
      !!contactsTable.virtualizedTableProps.sorting?.length;
    result.push(
      <ConfigureTableViewButton
        hasFilter={!!activeFiltersCount}
        filterCount={activeFiltersCount}
        hasSorting={hasSorting}
        onClick={() => setViewConfigDialogIsOpen(true)}
        hoverLabel={t("Configure View")}
        data-trackingid="contacts-table-configure-view"
      />
    );

    if (contactsTable.isDefaultViewModified) {
      result.push({
        label: t("Save as View"),
        variant: "outline",
        onClick: () => {
          setIsSaveAsView(true);
          setIsAddViewDialogOpen(true);
        },
        trackingId: "contacts-table-save-default-as-view",
      });
    }

    if (canCreate) {
      result.push({
        icon: Plus,
        label: t("Add Contact"),
        onClick: () => setIsCreateDrawerOpen(true),
        trackingId: "contacts-page-add-contact",
        testId: "contacts-add-contact",
      });
    }

    return result;
  }, [
    contactsTable.viewId,
    contactsTable.isDefaultViewModified,
    contactsTable.virtualizedTableProps.sorting?.length,
    canDelete,
    t,
    activeFiltersCount,
    canCreate,
    setIsAddViewDialogOpen,
  ]);

  if (!viewId) {
    return <Navigate replace to="./all" />;
  }

  return (
    <div className="flex flex-col flex-1">
      <div className="flex gap-2 items-center mb-4">
        <Tabs value={contactsTable.viewId} data-trackingid="contacts-table-view-selector-tabs">
          <TabsList>
            <TabsTrigger value="all" asChild>
              <Link to="/contacts/views/all">
                <Table className="w-4 h-4" />
                {t("All")}
              </Link>
            </TabsTrigger>
            {viewsToDisplay.map((view) => (
              <TabsTrigger key={view.id} value={view.id} asChild>
                <Link to={`/contacts/views/${view.id}`}>
                  <Table className="w-4 h-4" />
                  {view.title}
                </Link>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {!!rolledUpViews.length && (
          <Dialog open={isOtherViewsDialogOpen} onOpenChange={setIsOtherViewsDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="p-0 border-0 bg-transparent cursor-pointer text-muted-foreground text-sm"
                onClick={() => setIsOtherViewsDialogOpen(true)}
              >
                {t("and {{amount}} more...", { amount: rolledUpViews.length })}
              </button>
            </DialogTrigger>
            <DialogContent className="p-0 border-0 bg-transparent shadow-none [&>[data-slot=dialog-close]]:top-3 [&>[data-slot=dialog-close]]:right-3 [&>[data-slot=dialog-close]]:focus:ring-0 [&>[data-slot=dialog-close]]:focus:ring-offset-0">
              <Command className="rounded-lg border max-w-lg bg-popover">
                <CommandInput placeholder={t("Search views...")} className="h-12 p-3" />
                <CommandList>
                  <CommandEmpty>{t("No results found.")}</CommandEmpty>
                  {rolledUpViews.map((view) => (
                    <CommandItem key={view.id} value={view.id} asChild className="px-4 py-3 cursor-pointer">
                      <Link to={`/contacts/views/${view.id}`}>
                        <Table />
                        {view.title}
                      </Link>
                    </CommandItem>
                  ))}
                </CommandList>
              </Command>
            </DialogContent>
          </Dialog>
        )}
        <Dialog open={isAddViewDialogOpen} onOpenChange={setIsAddViewDialogOpen}>
          <DialogTrigger asChild>
            <button
              className="p-0 border-0 bg-transparent cursor-pointer outline-none focus:outline-none"
              data-trackingid="contacts-table-create-table-view-dialog-open"
            >
              <Plus className="w-6 h-6 text-muted-foreground hover:text-foreground transition-colors" />
            </button>
          </DialogTrigger>
          <DialogContent>
            <div className="space-y-1">
              <DialogTitle className="font-medium text-sm">
                {isSaveAsView ? t("Save as View") : t("Create New Table")}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {isSaveAsView
                  ? t("Enter a name for the new view. It will be saved with your current column configuration.")
                  : t("Enter a name for your new table view. Press Enter to add it to available views.")}
              </DialogDescription>
            </div>
            <form {...addViewForm.getFormProps()} className="flex flex-col gap-2">
              <Input
                placeholder={t("View name...")}
                {...addViewForm.getFieldProps("title")}
                onKeyDownCapture={(e) => {
                  if (e.key === "Enter" && addViewForm.values.title) {
                    e.stopPropagation();
                    addViewForm.submitForm();
                  }
                }}
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button onClick={() => setIsAddViewDialogOpen(false)} variant="outline">
                  {t("Cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={!addViewForm.isValid}
                  data-trackingid="contacts-table-create-table-view-dialog-confirm"
                >
                  {isSaveAsView ? t("Save View") : t("Add View")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <VirtualizedDataTable
        {...contactsTable.virtualizedTableProps}
        viewportHeight={{ offset: 270 }}
        actionButtons={actionButtons}
      />

      {/* Create Contact Drawer */}
      <Sheet open={isCreateDrawerOpen} onOpenChange={setIsCreateDrawerOpen}>
        <SheetContent className="w-96 p-0 overflow-y-auto z-[2020]">
          <SheetHeader>
            <SheetTitle>{t("Add New Contact")}</SheetTitle>
            <SheetDescription>{t("Create a new contact with the form below.")}</SheetDescription>
          </SheetHeader>
          <ContactForm
            mode="create"
            onSuccess={() => setIsCreateDrawerOpen(false)}
            onCancel={() => setIsCreateDrawerOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Configure Table View Dialog */}
      {!contactsTable.isLoadingViews && (
        <ContactsTableConfigDialog
          title={t("Configure {{tableViewName}}", {
            tableViewName: contactsTable.selectedView.title || t("All"),
          })}
          open={viewConfigDialogIsOpen}
          onOpenChange={(open) => setViewConfigDialogIsOpen(open)}
          columnConfigFormProps={{
            combinedFieldToColumnConfigMap: contactsTable.combinedFieldToColumnConfigMap,
            columnVisibility: contactsTable.columnVisibility,
            orderedVisibleColumnIds: contactsTable.orderedVisibleColumnIds,
          }}
          onApplyConfig={contactsTable.onApplyConfig}
          activeFilters={contactsTable.activeFilters}
          onClearAll={
            contactsTable.isDefaultViewModified
              ? () => {
                  contactsTable.resetDefaultView();
                  setViewConfigDialogIsOpen(false);
                }
              : undefined
          }
          isDirty={contactsTable.isDefaultViewModified}
        />
      )}

      {/* Delete View Confirm Dialog */}
      <DestructiveConfirmDialog
        onConfirm={() => {
          contactsTable.deleteTableViewMutation.mutateAsync(contactsTable.viewId);
          navigate("/contacts/views/all");
        }}
        loading={contactsTable.deleteTableViewMutation.isPending}
        open={deleteDialogIsOpen}
        onOpenChange={(open) => setDeleteDialogIsOpen(open)}
        description={t("This will permanently delete the view. This action cannot be undone.")}
      />
    </div>
  );
};
